/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { AxiosError, AxiosResponse } from "axios";

import { HTTP_METHOD, IRequestInfo } from "@nf/constants";
import { IErrorModel, toErrorModel } from "../../models";
import { HttpFactory } from "./HttpFactory";

export class HttpService {
  private factoryKey: string;

  constructor(factoryKey: string = "default") {
    this.factoryKey = factoryKey;
  }

  /**
   * Perform an HTTP request and return a typed Promise.
   *
   * @param requestInfo - Request configuration (method, url, axiosConfig, data, baseURL)
   */
  async request<T>(requestInfo: Partial<IRequestInfo<any>>): Promise<T> {
    const httpFactory = HttpFactory.getInstance(this.factoryKey);
    const httpClient = httpFactory.getHttpClient(requestInfo.baseURL);

    const { method, url = "", axiosConfig = {}, data } = requestInfo;

    let response: AxiosResponse<any>;

    try {
      switch (method) {
        case HTTP_METHOD.GET:
          response = await httpClient.get(url, axiosConfig);
          break;
        case HTTP_METHOD.POST:
          response = await httpClient.post(url, data, axiosConfig);
          break;
        case HTTP_METHOD.PUT:
          response = await httpClient.put(url, data, axiosConfig);
          break;
        case HTTP_METHOD.PATCH:
          response = await httpClient.patch(url, data, axiosConfig);
          break;
        case HTTP_METHOD.DELETE:
          response = await httpClient.delete(url, data ?? "", axiosConfig);
          break;
        case HTTP_METHOD.HEAD:
          response = await httpClient.head(url, axiosConfig);
          break;
        default:
          throw {
            Message: `Unsupported HTTP method: ${method}`,
          } as IErrorModel;
      }
    } catch (error) {
      throw HttpService.getError(error as AxiosError<any>);
    }

    return response.data as T;
  }

  private static getError(axiosError: AxiosError<any>): IErrorModel {
    if (axiosError.response) {
      const error: IErrorModel = {
        ...axiosError.response.data,
      };

      switch (axiosError.response.status) {
        case 401:
          if (!error.Message) {
            error.Message = `Unauthorized access.`;
          }
          break;
        case 403:
          if (!error.Message) {
            error.Message = `You don't have permissions to access.`;
          }
          break;
      }

      return error;
    }

    return {
      Message: axiosError.message,
    };
  }
}

/** HttpService instance for RedBeeMedia / general services */
export const httpService = new HttpService();

/** HttpService instance for Skymill services */
export const skymillHttpService = new HttpService("skymill");
